import "./Header.scss";
import { WmTabPanel } from "@watermarkinsights/ripple-react";
import Lightcast from "@/features/lightcast/Lightcast";
import ContinuousImprovement from "@/features/continuous-improvement/ContinuousImprovement";
import Strategy from "@/features/strategy/Strategy";
import InsightsTabComponent from "../tabComponent/InsightsTabComponent";
import { useIntl } from "react-intl";
import { useReducer, useEffect } from "react";
import Sse from "@/features/sse/Sse";
import FacultySuccess from "@/features/faculty-success/FacultySuccess";
import { sendRequest } from "@/common/http";
import { getDefaultReportingYear } from "./ReportingYears";
import {
  Institution,
  Product,
  ReportingYear,
  TabOption,
  defaultReportingYear
} from "../interfaces/Interface";
import { useNavigate } from "react-router";
import { useContext } from "react";
import { AppContext, AppContextInterface } from "@/contexts/AppContext";
import {
  checkIfFsProductIntegrated,
  checkIfCsProductIntegrated,
  checkIfSseProductIntegrated,
  checkIfSllProductIntegrated,
  checkIfCesProductIntegrated
} from "../helper";

interface OrgProfileContentState {
  tabOptions: TabOption[];
  reportingYears: ReportingYear[] | null;
  defaultReportingYear: ReportingYear;
  ryApiError: boolean;
}

interface OrgProfileAction {
  type: string;
  strategyTabOption: TabOption;
  laborMarketTabOption: TabOption;
  studentsTabOption: TabOption;
  facultyTabOption: TabOption;
  continuousImprovementTabOption: TabOption;
  selectedReportingYearUuid: string | null;
  data: ReportingYear[];
  ryApiError: boolean;
}

const defaultInitialTabOption: TabOption = {
  name: "",
  id: ""
};

const defaultAction: OrgProfileAction = {
  type: "",
  strategyTabOption: defaultInitialTabOption,
  laborMarketTabOption: defaultInitialTabOption,
  studentsTabOption: defaultInitialTabOption,
  facultyTabOption: defaultInitialTabOption,
  continuousImprovementTabOption: defaultInitialTabOption,
  selectedReportingYearUuid: "",
  data: [],
  ryApiError: false
};

const reducer = (state: OrgProfileContentState, action: OrgProfileAction) => {
  let tabOptions = state.tabOptions;
  switch (action.type) {
    case "addLightcastTab":
      tabOptions.splice(4, 0, action.laborMarketTabOption);

      return {
        ...state,
        tabOptions: tabOptions
      };
    case "addSseTab":
      let faculty = tabOptions.find((option: TabOption) => {
        return option.id == "faculty-insights";
      });

      if (faculty) {
        tabOptions.splice(3, 0, action.studentsTabOption);
      } else {
        tabOptions.splice(2, 0, action.studentsTabOption);
      }

      return {
        ...state,
        tabOptions: tabOptions
      };

    case "addFacultyTab":
      tabOptions.splice(2, 0, action.facultyTabOption);
      return {
        ...state,
        tabOptions: tabOptions
      };

    case "addContinuousImprovementTab":
      let ciTab = tabOptions.find((option: TabOption) => {
        return option.id == "continuous-improvement-insights";
      });

      ciTab
        ? tabOptions
        : tabOptions.splice(1, 0, action.continuousImprovementTabOption);

      return {
        ...state,
        tabOptions: tabOptions
      };
    case "addStrategyTab":
      let strategyTab = tabOptions.find((option: TabOption) => {
        return option.id == "institution-strategy";
      });
      strategyTab
        ? tabOptions
        : tabOptions.splice(0, 0, action.strategyTabOption);

      return {
        ...state,
        tabOptions: tabOptions
      };

    case "setReportingYears":
      const defaultReportingYear =
        getReportingYear(action.selectedReportingYearUuid, action.data) ||
        getDefaultReportingYear(state.defaultReportingYear, action.data);
      return {
        ...state,
        defaultReportingYear: defaultReportingYear,
        reportingYears: action.data
      };
    case "setRyApiError":
      return { ...state, ryApiError: action.ryApiError };
    default:
      return state;
  }
};

const getReportingYear = (
  selectedReportingYearUuid: string | null,
  reportingYears: ReportingYear[]
) => {
  if (
    selectedReportingYearUuid === undefined ||
    selectedReportingYearUuid === null
  ) {
    return undefined;
  } else {
    const reportingYear = reportingYears.filter(
      (reportingYear: ReportingYear) =>
        reportingYear.uuid === selectedReportingYearUuid
    );

    return reportingYear[0];
  }
};

const orderedTabIds = [
  "institution-strategy",
  "continuous-improvement-insights",
  "faculty-insights",
  "labor-market-insights",
  "students-insights"
];

const showContinuousImprovementTab = (
  institution: Institution,
  integratedProducts: Product[]
) => {
  return (
    institution?.isPssIntegrated ||
    checkIfCesProductIntegrated(integratedProducts) ||
    checkIfSllProductIntegrated(integratedProducts)
  );
};

export interface OrgProfileContentProps {
  setSelectedTabView: (tabId: string) => void;
  productView?: "planning" | "navigator";
}
export default function OrgProfileContent({
  setSelectedTabView,
  productView = "planning"
}: OrgProfileContentProps): JSX.Element {
  const { institution, selectedTab, onWmTabSelectedEvent, integratedProducts } =
    useContext<AppContextInterface>(AppContext);

  const navigate = useNavigate();

  const queryString = window.location.search;
  const param = new URLSearchParams(queryString);
  const selectedReportingYearUuid = param.get("reporting_year_uuid");

  const strategyLabel = useIntl().formatMessage({
    id: "app.orgProfileContent.strategy"
  });

  const laborMarketLabel = useIntl().formatMessage({
    id: "app.orgProfileContent.laborMarket"
  });

  const sseLabel = useIntl().formatMessage({
    id: "app.orgProfileContent.sseLabel"
  });

  const continuousImprovementLabel = useIntl().formatMessage({
    id: "app.orgProfileContent.countinousImprovement"
  });
  const facultyLabel = useIntl().formatMessage({
    id: "app.orgProfileContent.faculty"
  });

  const initialTabOptions: TabOption[] = [];

  const initialState: OrgProfileContentState = {
    tabOptions: initialTabOptions,
    reportingYears: null,
    defaultReportingYear: defaultReportingYear,
    ryApiError: false
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const applyResult = (result: ReportingYear[]) => {
      dispatch({
        ...defaultAction,
        type: "setReportingYears",
        data: result,
        selectedReportingYearUuid: selectedReportingYearUuid
      });
    };
    const onError = () => {
      dispatch({ ...defaultAction, type: "setRyApiError", ryApiError: true });
    };
    sendRequest(
      { uri: "/sa/reporting_years", camelize: true },
      applyResult,
      onError
    );
  }, []);

  useEffect(() => {
    const tabParam = new URLSearchParams(window.location.search).get("tab");
    if (state.tabOptions.length > 0 && setSelectedTabView && !tabParam) {
      const firstTabId = orderedTabIds.find((tabId: string) => {
        return state.tabOptions.some(
          (option: TabOption) => option.id === tabId
        );
      });

      if (firstTabId) setSelectedTabView(firstTabId);
    }
  }, [state.tabOptions.length]);

  useEffect(() => {
    if (institution) {
        setStrategyTab();
        
      if (showContinuousImprovementTab(institution, integratedProducts)) {
        setContinuousImprovementTab();
      }

      if (checkIfFsProductIntegrated(integratedProducts)) {
        setFacultyTab();
      }

      if (checkIfSseProductIntegrated(integratedProducts)) {
        setSseTab();
      }
    }
  }, [integratedProducts]);

  const setLightcastTab = () => {
    dispatch({
      ...defaultAction,
      type: "addLightcastTab",

      laborMarketTabOption: {
        name: laborMarketLabel,
        id: "labor-market-insights"
      }
    });
  };

  const setStrategyTab = () => {
    dispatch({
      ...defaultAction,
      type: "addStrategyTab",
      strategyTabOption: {
        name: strategyLabel,
        id: "institution-strategy"
      }
    });
  };

  const setSseTab = () => {
    dispatch({
      ...defaultAction,
      type: "addSseTab",
      studentsTabOption: {
        name: sseLabel,
        id: "students-insights"
      }
    });
  };
  const setFacultyTab = () => {
    dispatch({
      ...defaultAction,
      type: "addFacultyTab",
      facultyTabOption: {
        name: facultyLabel,
        id: "faculty-insights"
      }
    });
  };

  const setContinuousImprovementTab = () => {
    dispatch({
      ...defaultAction,
      type: "addContinuousImprovementTab",
      continuousImprovementTabOption: {
        name: continuousImprovementLabel,
        id: "continuous-improvement-insights"
      }
    });
  };

  const onWmTabSelect = (event: any) => {
    let params = new URLSearchParams();
    params.append("tab", event.detail.tabId);
    const queryString = window.location.search;
    const param = new URLSearchParams(queryString);
    const locale = param.get("locale");
    if (locale !== null) {
      params.append("locale", locale);
    }
    navigate(`?${params.toString()}`, { replace: true });

    onWmTabSelectedEvent(event);
  };

  return (
    <>
      <div className="tabbar -tabbar">
        <InsightsTabComponent
          selectedTab={selectedTab}
          onWmTabSelected={onWmTabSelect}
          tabOption={state.tabOptions}
        />
      </div>
      <WmTabPanel
        className="tab-panel"
        id="strategy"
        tabId="institution-strategy"
      >
        {institution && (
            <Strategy
              reportingYears={state.reportingYears}
              defaultReportingYear={state.defaultReportingYear}
              ryApiError={state.ryApiError}
              setStrategyTab={setStrategyTab}
            />
          )}
      </WmTabPanel>

      <WmTabPanel
        className="tab-panel"
        id="continuous-improvement"
        tabId="continuous-improvement-insights"
      >
        {institution &&
          integratedProducts &&
          showContinuousImprovementTab(institution, integratedProducts) && (
            <ContinuousImprovement
              reportingYears={state.reportingYears}
              defaultReportingYear={state.defaultReportingYear}
              ryApiError={state.ryApiError}
              organizationView={false}
              productView={productView}
            />
          )}
      </WmTabPanel>
      <WmTabPanel className="tab-panel" id="faculty" tabId="faculty-insights">
        {checkIfFsProductIntegrated(integratedProducts) && (
          <FacultySuccess
            reportingYears={state.reportingYears}
            defaultReportingYear={state.defaultReportingYear}
            ryApiError={state.ryApiError}
            organizationView={false}
          />
        )}
      </WmTabPanel>

      <WmTabPanel className="tab-panel" id="students" tabId="students-insights">
        {checkIfSseProductIntegrated(integratedProducts) && <Sse />}
      </WmTabPanel>
      {institution?.isPssIntegrated && (
        <WmTabPanel
          className="tab-panel"
          id="labor-market"
          tabId="labor-market-insights"
        >
          <Lightcast setLightcastTab={setLightcastTab} />
        </WmTabPanel>
      )}
    </>
  );
}
